<template>
  <v-container fluid>
    <v-card :loading="loading">
      <v-card-title>
        <h2>
          <v-btn depressed class="mx-2" fab small :to="{ name: 'vendedor-list' }">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span v-if="loading">Carregando...</span>
          <span v-else>{{id !== "" ? `Alterar vendedor: ${id} - ${geral.nome}` : "Novo vendedor"}}</span>
        </h2>
      </v-card-title>
      <form @submit.prevent="submitForm">
        <v-tabs v-model="tab" grow>
          <v-tab>
            <v-icon
              left
              color="error"
              v-if="$v.geral.$anyDirty && $v.geral.$invalid"
              class="mdi mdi-alert-circle"
            ></v-icon>
            <span>Geral</span>
          </v-tab>
          <v-tab>Endereço</v-tab>
          <v-tab>Documentos</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card-text>
              <v-radio-group v-model="tipoVendedor" row>
                <v-radio label="Vendedor externo" value="externo" />
                <v-radio label="Funcionário" value="funcionario" />
              </v-radio-group>

              <v-autocomplete
                v-if="tipoVendedor === 'funcionario'"
                v-model="funcionarioSelecionado"
                :items="funcionarios"
                :search-input.sync="pesquisa"
                no-filter
                :loading="pesquisando"
                return-object
                label="Funcionário"
                clearable
                no-data-text="Nenhum funcionário encontrado"
                placeholder="Pesquise por nome ou número da matrícula"
                prepend-icon="mdi-database-search"
              >
                <template v-slot:item="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.matricula}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.matricula}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-text-field
                v-model="geral.nome"
                v-touch="geral.nome"
                :error-messages="nomeErrors"
                :counter="40"
                label="Nome"
                :disabled="tipoVendedor === 'funcionario'"
              ></v-text-field>
              <v-text-field
                v-model="geral.email"
                v-touch="geral.email"
                :error-messages="emailErrors"
                :counter="140"
                label="E-mail"
                :disabled="tipoVendedor === 'funcionario'"
              ></v-text-field>
              <v-text-field
                v-model="geral.telefone"
                v-touch="geral.telefone"
                v-mask="'phone'"
                :error-messages="telefoneErrors"
                maxlength="15"
                label="Telefone"
                :disabled="tipoVendedor === 'funcionario'"
              ></v-text-field>
              <v-autocomplete
                v-model="geral.id_usuario"
                clearable
                label="Usuário associado"
                hint="Este campo interfere na visibiliadade do cadastro de clientes"
                persistent-hint
                :items="usuarios"
                item-value="id"
              >
                <template v-slot:item="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.login}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:selection="{item}">
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <strong>{{item.login}}</strong>
                    </v-list-item-subtitle>
                    <v-list-item-title>{{item.nome}}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <endereco-form v-model="endereco" :disabled="tipoVendedor === 'funcionario'" />
            </v-card-text>
          </v-tab-item>

          <v-tab-item>
            <lista-documentos
              :url-base="urlDocumento"
              :id-proprietario="proprietarioDocumento"
              v-model="documentos"
              :alterar="tipoVendedor === 'externo'"
            />
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-btn depressed color="primary" @click="submit" :loading="loading">Salvar</v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import { validationMixin } from "vuelidate";
import { required, maxLength, email } from "vuelidate/lib/validators";
import ListaDocumentos from "../components/documentos/ListaDocumentos";
import EnderecoForm from "../components/enderecos/EnderecoForm";
import { unmaskPhone, unmaskCEP } from "../lib/masks";

const modelo = {
  geral: {
    id_funcionario: "",
    id_usuario: null,
    nome: "",
    email: "",
    telefone: "",
  },
  endereco: {
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    id_cidade: null,
    cep: "",
    cidade: null,
  },
  documentos: [],
};
export default {
  components: { ListaDocumentos, EnderecoForm },
  mixins: [validationMixin],

  validations: {
    geral: {
      nome: { required, maxLength: maxLength(40) },
      email: { maxLength: maxLength(140), email },
      telefone: { maxLength: maxLength(20) },
    },
  },

  data() {
    return {
      ..._.cloneDeep(modelo),
      id: "",
      tipoVendedor: "externo",
      tab: null,
      loading: false,
      pesquisa: null,
      pesquisando: false,
      funcionarioSelecionado: null,
    };
  },

  computed: {
    nomeErrors() {
      const errors = [];
      if (!this.$v.geral.nome.$dirty) return errors;
      !this.$v.geral.nome.required && errors.push("Nome é obrigatório.");
      !this.$v.geral.nome.maxLength && errors.push("Somente 40 caracteres");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.geral.email.$dirty) return errors;
      !this.$v.geral.email.email && errors.push("Informe um email válido");
      !this.$v.geral.email.maxLength && errors.push("Somente 140 caracteres");
      return errors;
    },
    telefoneErrors() {
      const errors = [];
      if (!this.$v.geral.telefone.$dirty) return errors;
      !this.$v.geral.telefone.maxLength && errors.push("Somente 20 caracteres");
      return errors;
    },
    urlDocumento() {
      return this.tipoVendedor === "funcionario"
        ? "funcionarios"
        : "vendedores";
    },
    proprietarioDocumento() {
      return this.tipoVendedor === "funcionario"
        ? this.geral.id_funcionario
        : this.id;
    },
    ...mapState({
      funcionarios: (state) => state.funcionario.Funcionarios,
      usuarios: (state) => state.usuarios.usuarios,
    }),
  },

  methods: {
    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;

        let dados;
        if (this.tipoVendedor === "funcionario") {
          dados = {
            id: this.id,
            id_funcionario: this.geral.id_funcionario,
            id_usuario: this.geral.id_usuario,
          };
        } else {
          delete this.geral.id_funcionario;
          dados = {
            id: this.id,
            id_usuario: this.id_usuario,
            ...this.geral,
            ...this.endereco,
            telefone: unmaskPhone(this.geral.telefone),
            cep: unmaskCEP(this.endereco.cep),
          };
        }
        try {
          const vendedor = await this.$store.dispatch("vendedor/gravar", dados);
          if (this.tipoVendedor === "externo" && this.documentos.length > 0) {
            await this.$store.dispatch("documento/gravar", {
              urlBase: "vendedores",
              IDProprietario: vendedor.id,
              documentos: this.documentos,
            });
          }
          this.$router.replace({ path: "lista", name: "vendedor-list" });
        } catch (err) {
          console.log(err);
          this.$store.commit("ui/setSnackbar", {
            text: "Não foi possível salvar o vendedor",
          });
        } finally {
          this.loading = false;
        }
      }
    },
    limpar() {
      const m = _.cloneDeep(modelo);
      this.geral = m.geral;
      this.endereco = m.endereco;
      this.documentos = m.documentos;
      this.funcionarioSelecionado = null;
    },
    pesquisarFuncionarios: _.debounce(async function () {
      this.pesquisando = true;
      await this.$store.dispatch("funcionario/listar", {
        pesquisa: this.pesquisa,
      });
      this.pesquisando = false;
    }, 300),
  },
  async mounted() {
    try {
      await this.$store.dispatch("usuarios/listar", {});
    } catch (err) {
      console.log(err);
      this.$store.commit("ui/setSnackbar", {
        text: "Não foi possível carregar a lista de usuários",
      });
    }
    this.$store.commit("funcionario/carregar", []);
    this.$store.commit("ui/changeToolbar", {
      title: "Cadastro de vendedores",
    });
    if (this.$route.params.id) {
      this.id = this.$route.params.id;

      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "vendedor/carregar",
          this.id
        );
        let funcionario = null;
        if (response.data.id_funcionario !== null) {
          this.tipoVendedor = "funcionario";
          const responseFuncionario = await this.$store.dispatch(
            "funcionario/carregarFuncionario",
            response.data.id_funcionario
          );
          funcionario = responseFuncionario.data;
          funcionario.id_usuario = response.data.id_usuario;
          this.$store.commit("funcionario/carregar", [funcionario]);
        }
        this.$nextTick(() => {
          this.funcionarioSelecionado = funcionario;
          this.geral = {
            id_funcionario: response.data.id_funcionario,
            id_usuario: response.data.id_usuario,
            nome: response.data.nome,
            email: response.data.email,
            telefone: response.data.telefone,
          };
          this.endereco = {
            endereco: response.data.endereco,
            numero: response.data.numero,
            complemento: response.data.complemento,
            bairro: response.data.bairro,
            id_cidade: response.data.id_cidade,
            cep: response.data.cep,
            cidade: response.data.cidade,
          };
        });
      } catch (err) {
        console.log(err);
        this.$store.commit("ui/setSnackbar", {
          text: "Não foi possível carregar o vendedor",
        });
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    tipoVendedor() {
      this.limpar();
    },
    pesquisa() {
      this.pesquisarFuncionarios();
    },
    funcionarioSelecionado(func) {
      if (func) {
        this.geral = {
          id_funcionario: func.id,
          id_usuario: func.id_usuario,
          nome: func.nome,
          email: func.email,
          telefone: func.telefone,
        };
        this.endereco = {
          cep: func.cep,
          endereco: func.endereco,
          numero: func.numero,
          complemento: func.complemento,
          bairro: func.bairro,
          id_cidade: func.id_cidade,
          cidade: func.cidade,
        };
      } else {
        this.limpar();
      }
    },
  },
};
</script>
